// Supabase Storage upload with progress reporting and retry.
//
// `supabase.storage.upload()` is a single `fetch()`: no progress events, and no retry.
// For a consultation recording that is the wrong shape twice over.
//
//   * A 33 MB file on a clinic uplink is a minute or more during which the clinician
//     sees only "Saving recording…" — no bytes, no estimate, no way to tell a slow
//     upload from a hung one. The reported symptom was a 5-minute silent wait.
//   * One dropped connection loses the whole upload. The recording then exists only as
//     a blob URL in the tab, behind a "download a backup before leaving" warning, and
//     closing the tab destroys it. A transient network blip should not be able to cost
//     a clinician a session recording.
//
// XHR is used instead of fetch purely because `xhr.upload.onprogress` exists and the
// fetch Streams equivalent is not available across the browsers Scribe supports
// (Chrome, Firefox, Safari, Edge — see Settings → Help). The request itself is the same
// one supabase-js builds: POST /storage/v1/object/<bucket>/<path>, bearer the caller's
// session, the object body raw.

import { supabase, supabaseUrl, supabaseAnonKey } from '../supabaseClient.js';

/**
 * Aggregate several concurrent uploads into one figure for a single progress bar.
 *
 * The file pickers upload a whole selection in parallel (`Promise.all` over the files),
 * so per-file bars would race each other in one indicator. Register each file with
 * `add` *before* its upload starts so the denominator is the full selection from the
 * first frame rather than growing as uploads report in — a bar whose total keeps rising
 * looks like it is going backwards.
 *
 * `onChange` receives `{ loaded, total, files, attempt, attempts }`, or null once
 * `done()` clears the batch. `attempt` is the highest across the batch, so one file
 * retrying is visible without implying every file restarted.
 */
export function createBatchProgress(onChange) {
  const parts = new Map();
  const emit = () => {
    let loaded = 0;
    let total = 0;
    let attempt = 1;
    let attempts = 1;
    parts.forEach((part) => {
      loaded += part.loaded;
      total += part.total;
      if (part.attempt > attempt) { attempt = part.attempt; attempts = part.attempts; }
    });
    onChange({ loaded, total, files: parts.size, attempt, attempts });
  };
  return {
    add(key, total) {
      parts.set(key, { loaded: 0, total, attempt: 1, attempts: 1 });
      emit();
    },
    onProgress: (key) => ({ loaded, total, attempt, attempts }) => {
      parts.set(key, { loaded, total, attempt, attempts });
      emit();
    },
    done() {
      parts.clear();
      onChange(null);
    },
  };
}

/** A failed upload. `status` is the HTTP status, or 0 for a transport failure. */
export class StorageUploadError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = 'StorageUploadError';
    this.status = status;
  }
}

// Worth retrying: the request never reached a verdict, or the verdict was explicitly
// temporary. Everything else (401 expired session, 403 RLS, 409 exists, 413 too large)
// will fail identically however many times it is sent, and retrying only delays the
// error message the clinician needs to act on.
const isRetryable = (status) =>
  status === 0 || status === 408 || status === 429 || (status >= 500 && status < 600);

const DEFAULT_RETRY_DELAYS = [1000, 3000, 7000];

const abortError = () => {
  const error = new Error('Upload cancelled');
  error.name = 'AbortError';
  return error;
};

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(abortError());
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });

// Storage answers errors as JSON ({ message } / { error }), but a proxy or gateway in
// front of it answers with HTML. Never surface a page of markup as an error message.
const readErrorMessage = (xhr) => {
  try {
    const parsed = JSON.parse(xhr.responseText);
    const message = parsed?.message || parsed?.error;
    if (typeof message === 'string' && message) return message;
  } catch {
    /* not JSON */
  }
  return `Upload failed (${xhr.status || 'network error'})`;
};

function sendOnce({ url, body, headers, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));

    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    const done = () => signal?.removeEventListener('abort', onAbort);

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        // `total` is 0 until the browser knows the body length; report only real numbers
        // so the caller can tell "no progress yet" from "0 bytes sent".
        if (event.lengthComputable) onProgress({ loaded: event.loaded, total: event.total });
      };
    }
    xhr.onload = () => {
      done();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new StorageUploadError(readErrorMessage(xhr), xhr.status));
    };
    // A transport failure carries no status: status 0 marks it retryable.
    xhr.onerror = () => { done(); reject(new StorageUploadError('Network error during upload', 0)); };
    xhr.ontimeout = () => { done(); reject(new StorageUploadError('Upload timed out', 0)); };
    xhr.onabort = () => { done(); reject(abortError()); };

    xhr.send(body);
  });
}

/**
 * Upload `body` to `bucket`/`path`, reporting progress and retrying transient failures.
 *
 * `onProgress({ loaded, total, attempt, attempts })` fires as bytes leave the browser;
 * `loaded` restarts at 0 on each attempt, because a retry re-sends the whole object.
 * Rejects with `StorageUploadError` (`.status`), or an `AbortError` if `signal` fires.
 */
export async function uploadWithProgress(bucket, path, body, {
  contentType,
  cacheControl = '3600',
  upsert = false,
  onProgress,
  signal,
  maxAttempts = 4,
  retryDelays = DEFAULT_RETRY_DELAYS,
} = {}) {
  const { data: { session } = {} } = await supabase.auth.getSession();
  const accessToken = session?.access_token || supabaseAnonKey;
  const url = `${supabaseUrl}/storage/v1/object/${bucket}/${path.split('/').map(encodeURIComponent).join('/')}`;

  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      await sendOnce({
        url,
        body,
        headers: {
          authorization: `Bearer ${accessToken}`,
          apikey: supabaseAnonKey,
          'cache-control': `max-age=${cacheControl}`,
          // A retry re-sends bytes we already tried to write, so after the first attempt
          // an object left behind by a half-finished upload must be overwritable — the
          // caller's `upsert: false` guards against a *different* file at this key, and
          // a retry is by definition the same one.
          'x-upsert': String(upsert || attempt > 1),
          ...(contentType ? { 'content-type': contentType } : {}),
        },
        onProgress: onProgress
          ? ({ loaded, total }) => onProgress({ loaded, total, attempt, attempts: maxAttempts })
          : undefined,
        signal,
      });
      return { path };
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      lastError = error;
      const canRetry = isRetryable(error?.status ?? 0) && attempt < maxAttempts;
      if (!canRetry) throw error;
      await sleep(retryDelays[Math.min(attempt - 1, retryDelays.length - 1)], signal);
    }
  }
  throw lastError;
}
